import React from 'react';
import { useTranslation } from 'react-i18next';

import type { SelectOption } from '@/nova/components';

import * as S from './EasyFootSelect.styles';

export interface EasyFootSelectProps {
  /** Accessible label (i18n key) for the control. */
  label: string;
  value: string;
  /** Options may carry an optional `description` node, rendered under the label inside the flyout (used by the
   *  video-model picker to explain each model); plain options render just their label as before. */
  options: (SelectOption & { description?: React.ReactNode })[];
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * The footer dropdown for the easy modal: a 48px trigger (matching the footer buttons) whose options list opens
 * UPWARD, so a control pinned to the bottom of the sheet never opens off the bottom of the viewport the way a
 * native `<select>` does. Rounded + soft-elevated to match the studio's other popovers. Labels run through
 * `t()` exactly like the shared {@link Select}, so callers pass the same option shape.
 */
export const EasyFootSelect = ({ label, value, options, onChange, disabled }: EasyFootSelectProps): React.ReactElement => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const current = options.find((o) => o.value === value);

  // Close on an outside click or Escape while open. Bound only while open so it never leaks listeners.
  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <S.Root ref={rootRef}>
      <S.Trigger
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t(label)}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <S.Value raw text={current ? t(current.label) : value} />
        <S.Chevron $open={open} viewBox="0 0 12 12" aria-hidden="true">
          <polyline points="2,4 6,8 10,4" strokeLinecap="round" strokeLinejoin="round" />
        </S.Chevron>
      </S.Trigger>
      {open ? (
        <S.Menu role="listbox" aria-label={t(label)}>
          {options.map((o) => (
            <S.Option
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              $selected={o.value === value}
              $rich={Boolean(o.description)}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              <S.OptionText raw text={t(o.label)} />
              {o.description ?? null}
            </S.Option>
          ))}
        </S.Menu>
      ) : null}
    </S.Root>
  );
};

export default EasyFootSelect;
